/**
 * 注意 router / views / navigation 必須同時加入才行
 *
 * 選單項目已直接內聯於本檔（原 src/navigation/modules/{admin,shepherd}/*.ts
 * 已合併進來，不再分檔）。新增/修改選單項目直接改下方陣列即可。
 */

import {
    computed
} from "vue";

import {
    useRoute
} from "vue-router";

import {
    RouteNameEnum
} from "@/v2/enumerate/route";

import {
    EnumPortalName
} from "@/enum/portal";

/**
 * 我的教會 (/admin) 選單
 * 注意: id 才是排序與識別依據 (見 adminSortList); 陣列順序不影響結果。
 */
const adminNavigationItem = [
    //1 控制面板
    {
        id: 1,
        right: false,
        subject: '控制面板',
        route: 'AdminDashboard',
        icon: 'monitor',
        children: [],
        //無法歸類的放首頁
        attached: [
            "AdminPersonal",
            "AdminDashboardStatistics"
        ],
    },
    //17 公吿管理 / 公吿欄
    {
        id: 17,
        right: true,
        subject: '公吿管理',
        route: 'AdminBillboard',
        icon: 'briefcase',
        children: [],
        attached: [
            'AdminBillboardCreate',
            'AdminBillboardEdit'
        ]
    },
    {
        id: 17,
        right: false,
        subject: '公吿欄',
        route: 'Billboard',
        icon: 'briefcase',
        children: [],
        attached: [
            'BillboardEdit'
        ]
    },
    //3 教會管理
    {
        id: 3,
        right: true,
        subject: '教會管理',
        route: 'AdminChurch',
        icon: 'home',
        children: [],
        attached: [
            'AdminChurchCreate',
            'AdminChurchEdit'
        ]
    },
    //2 管理員帳號管理 (原 modules/admin/cycle.ts)
    {
        id: 2,
        right: true,
        subject: '管理員帳號管理',
        route: 'AdminUser',
        icon: 'user',
        children: [],
        attached: [
            'AdminUserCreate',
            'AdminUserEdit',
        ]
    },
    //4 會友管理
    {
        id: 4,
        right: true,
        subject: '會友管理',
        route: 'AdminClient',
        icon: 'profile-2user',
        children: [],
        attached: [
            'AdminClientCreate',
            'AdminClientEdit',
            'AdminClientDonate',
            'AdminClientDonateEdit',
        ]
    },
    //5 我的教會文章管理
    {
        id: 5,
        right: true,
        subject: '我的教會文章管理',
        route: 'AdminMy',
        icon: 'cup',
        children: [],
        attached: [
            'AdminMyCreate',
            'AdminMyEdit',
            'AdminMyCopy',
            'AdminMyMindCatcher',
            'AdminMyComment',
            'AdminMyNotification',
            'AdminMyNotificationCreate',
            'AdminMyNotificationEdit'
        ]
    },
    //6 心靈捕手文章管理
    {
        id: 6,
        right: true,
        subject: '心靈捕手文章管理',
        route: null,
        icon: 'star',
        children: [{
            id: 6001,
            right: true,
            subject: '心靈捕手文章管理',
            route: 'AdminSoul',
            attached: [
                'AdminSoulCreate',
                'AdminSoulEdit',
                'AdminSoulCopy',
                'AdminSoulMindCatcher',
                'AdminSoulComment',
                'AdminSoulNotification',
                'AdminSoulNotificationCreate',
                'AdminSoulNotificationEdit'
            ]
        }, {
            id: 6011,
            right: true,
            subject: '心靈捕手分類管理',
            route: 'AdminSoulCategory',
            attached: [
                'AdminSoulCategoryCreate',
                'AdminSoulCategoryEdit'
            ]
        }],
        attached: []
    },
    //7 每日靈糧文章管理
    {
        id: 7,
        right: true,
        subject: '每日靈糧文章管理',
        route: 'AdminDaily',
        icon: 'wifi',
        children: [],
        attached: [
            'AdminDailyCreate',
            'AdminDailyEdit',
            'AdminDailyCopy',
            'AdminDailyMindCatcher',
            'AdminDailyComment',
            'AdminDailyNotification',
            'AdminDailyNotificationCreate',
            'AdminDailyNotificationEdit'
        ]
    },
    //8 圖庫管理
    {
        id: 8,
        right: true,
        subject: '圖庫管理',
        route: null,
        icon: 'gallery',
        children: [{
            id: 8001,
            right: true,
            subject: '圖庫清單',
            route: 'AdminGallery',
            attached: []
        }, {
            id: 8011,
            right: true,
            subject: '圖庫核准',
            route: 'AdminGalleryAccept',
            attached: []
        }, {
            id: 8021,
            right: true,
            subject: '圖庫檢舉',
            route: 'AdminGalleryAlarm',
            attached: []
        }],
        attached: []
    },
    //9 推播管理
    {
        id: 9,
        right: true,
        subject: '推播管理',
        route: RouteNameEnum.ADMIN_NOTIFICATION,
        icon: 'sound',
        children: [],
        attached: [
            RouteNameEnum.ADMIN_NOTIFICATION_CREATE,
            RouteNameEnum.ADMIN_NOTIFICATION_EDIT,
            RouteNameEnum.ADMIN_NOTIFICATION_EDIT_HISTORY
        ]
    },
    //10 行事曆管理
    {
        id: 10,
        right: true,
        subject: '行事曆管理',
        route: null,
        icon: 'clipboard',
        children: [{
            id: 10011,
            right: true,
            subject: '服事群組',
            route: RouteNameEnum.ADMIN_EVENT_TEAM,
            attached: [
                RouteNameEnum.ADMIN_EVENT_TEAM_CREATE,
                RouteNameEnum.ADMIN_EVENT_TEAM_EDIT,
                RouteNameEnum.ADMIN_EVENT_TEAM_EDIT_HISTORY
            ]
        }, {
            id: 10001,
            right: true,
            subject: '服事清單',
            route: RouteNameEnum.ADMIN_EVENT,
            attached: [
                RouteNameEnum.ADMIN_EVENT_CREATE,
                RouteNameEnum.ADMIN_EVENT_EDIT
            ]
        }, {
            //教會行事曆：全教會可見的行程（含 Google 匯入），新增/編輯都在清單頁的抽屜完成
            id: 21001,
            right: true,
            subject: '教會行事曆',
            route: RouteNameEnum.ADMIN_CALENDAR,
            attached: []
        }],
        attached: []
    },
    //11 奉獻管理
    {
        id: 11,
        right: true,
        subject: '奉獻管理',
        route: 'AdminDonate',
        icon: 'heart',
        children: [],
        attached: [
            'AdminDonateEdit'
        ]
    },
    //12 教學管理
    {
        id: 12,
        right: true,
        subject: '教學管理',
        route: 'AdminTeach',
        icon: 'lamp',
        children: [],
        attached: [
            'AdminTeachCreate',
            'AdminTeachEdit'
        ]
    },
    //20 啟動圖排程
    {
        id: 20,
        right: true,
        subject: '啟動圖排程',
        route: 'AdminSplash',
        icon: 'image',
        children: [],
        attached: []
    },
    //13 循環管理 (原 modules/admin/user.ts)
    {
        id: 13,
        right: true,
        subject: '循環管理',
        route: 'AdminCycle',
        icon: 'repeat',
        children: [],
        attached: [
            'AdminCycleCreate',
            'AdminCycleEdit',
            'AdminCycleHistory'
        ]
    },
    //15 客服管理
    {
        id: 15,
        right: true,
        subject: '客服管理',
        route: null,
        icon: 'call-calling',
        children: [{
            id: 15001,
            right: true,
            subject: '帳號紀錄',
            route: 'AdminSupportHistory',
            attached: []
        }, {
            id: 15011,
            right: true,
            subject: '意見與建議',
            route: 'AdminSupportFeedback',
            attached: [
                'AdminSupportFeedbackEdit'
            ]
        }, {
            id: 15021,
            right: true,
            subject: '請我們喝咖啡',
            route: 'AdminSupportCoffee',
            attached: [
                'AdminSupportCoffeeEdit'
            ]
        }],
        attached: []
    },
    //16 歷程管理
    {
        id: 16,
        right: true,
        subject: '歷程管理',
        route: null,
        icon: 'notification-1',
        children: [{
            id: 16001,
            right: true,
            subject: '攔截紀錄',
            route: 'AdminCourseException',
            attached: [
                'AdminCourseExceptionEdit'
            ]
        }, {
            id: 16011,
            right: true,
            subject: '存取紀錄',
            route: 'AdminCourseAccess',
            attached: [
                'AdminCourseAccessEdit'
            ]
        }, {
            id: 16021,
            right: true,
            subject: '授權紀錄',
            route: 'AdminCourseAuthorize',
            attached: [
                'AdminCourseAuthorizeEdit'
            ]
        }],
        attached: []
    },
    //22 App 錯誤追蹤（僅總會）
    {
        id: 22,
        right: true,
        subject: 'App 錯誤追蹤',
        route: 'AdminAppError',
        icon: 'danger',
        children: [],
        attached: [
            'AdminAppErrorEdit'
        ]
    },
    //18 活動管理
    {
        id: 18,
        right: true,
        subject: '活動管理',
        route: null,
        icon: 'save-2',
        children: [{
            id: 18001,
            right: true,
            subject: '活動清單',
            route: 'AdminActivity',
            attached: [
                'AdminActivityCreate',
                'AdminActivityEdit',
                'AdminActivityEditRegister',
                'AdminActivityEditRegistered',
                'AdminActivityEditRegisteredEdit',
                'AdminActivityEditCheckin'
            ]
        }, {
            id: 18011,
            right: true,
            subject: '分類管理',
            route: 'AdminActivityCategory',
            attached: [
                'AdminActivityCategoryCreate',
                'AdminActivityCategoryEdit'
            ]
        }],
        attached: []
    },
    //19 直播管理
    //
    //⚠️ 暫時隱藏，不是刪除。只把選單註解掉，資料庫（core_admin_permission_group / _template
    //   的直播權限）與後端一律不動 —— 那些資料還在，之後要開回來只要把這段解除註解。
    //
    //   還沒動的另外兩處，需要一併隱藏時再處理：
    //     1. router/modules/admin/live.js（路由仍註冊）→ 直接打網址還是進得去
    //     2. hook/usePermission.js 的 getAvailableRight()（權限設定畫面仍列出直播的權限項目）
    //
    //   下面 useNavigationItem 的 adminSortList 刻意保留 19：那只是排序用的 id 序列，
    //   陣列裡沒有 id 19 時 indexOf 找不到、不會多生出項目；留著是為了解除註解時順序自動回到原位。
    /*
    {
        id: 19,
        right: true,
        subject: '直播管理',
        route: null,
        icon: 'radar',
        children: [{
            id: 19001,
            right: true,
            subject: '直播清單',
            route: 'AdminLive',
            attached: [
                'AdminLiveCreate',
                'AdminLiveEdit',
                'AdminLiveEditComment',
                'AdminLiveEditSecure'
            ]
        }, {
            id: 19011,
            right: true,
            subject: '分類管理',
            route: 'AdminLiveCategory',
            attached: [
                'AdminLiveCategoryCreate',
                'AdminLiveCategoryEdit'
            ]
        }],
        attached: []
    }
    */
]

/**
 * 牧養系統 (/shepherd) 選單
 */
const shepherdNavigationItem = [
    //51 控制面板
    {
        id: 51,
        right: false,
        subject: '控制面板',
        route: 'ShepherdDashboard',
        icon: 'monitor',
        children: [],
        //無法歸類的放首頁
        attached: [],
    },
    //52 新朋友管理
    {
        id: 52,
        right: true,
        subject: '新朋友管理',
        route: 'ShepherdFriend',
        icon: 'user-search',
        children: [],
        attached: [
            "ShepherdFriendEdit"
        ]
    },
    //53 會友管理
    {
        id: 53,
        right: true,
        subject: '會友管理',
        route: 'ShepherdClient',
        icon: 'profile-2user',
        children: [],
        attached: [
            "ShepherdClientEdit",
            "ShepherdClientAdvance",
            "ShepherdClientCourse",
            "ShepherdClientAttend",
            "ShepherdClientAttendEdit"
        ]
    },
    //54 族群管理
    {
        id: 54,
        right: false,
        subject: '族群管理',
        route: 'ShepherdTeam',
        icon: 'category',
        children: [],
        //無法歸類的放首頁
        attached: [
            "ShepherdTeamAttend",
            "ShepherdTeamStatistics",
            "ShepherdTeamStatisticsAttend",
            "ShepherdTeamStatisticsEvent",
            "ShepherdTeamStatisticsGender",
            "ShepherdTeamStatisticsYear",
            "ShepherdTeamStatisticsDeacon",
            "ShepherdTeamStatisticsEducation",
            "ShepherdTeamStatisticsOccupation",
            "ShepherdTeamStatisticsMarital",
            "ShepherdTeamStatisticsBaptized"
        ],
    },
    //55 聚會管理
    {
        id: 55,
        right: true,
        subject: '聚會管理',
        route: null,
        icon: 'audio',
        children: [{
            id: 55001,
            right: true,
            subject: '聚會清單',
            route: RouteNameEnum.SHEPHERD_ATTEND,
            attached: [
                RouteNameEnum.SHEPHERD_ATTEND_CREATE,
                RouteNameEnum.SHEPHERD_ATTEND_EDIT,
                RouteNameEnum.SHEPHERD_ATTEND_EDIT_SCHEDULE,
                RouteNameEnum.SHEPHERD_ATTEND_EDIT_SCHEDULE_EDIT,
                RouteNameEnum.SHEPHERD_ATTEND_EDIT_HISTORY,
                RouteNameEnum.SHEPHERD_ATTEND_EDIT_HISTORY_EDIT
            ]
        }, {
            id: 55011,
            right: true,
            subject: '分類管理',
            route: RouteNameEnum.SHEPHERD_ATTEND_CATEGORY,
            attached: [
                RouteNameEnum.SHEPHERD_ATTEND_CATEGORY_CREATE,
                RouteNameEnum.SHEPHERD_ATTEND_CATEGORY_EDIT
            ]
        }],
        attached: []
    }
]

/**
 * 榮耀堂首頁 (/glory) 選單 —— org=4 專屬
 *
 * 這一組的可見性不靠這裡的 right, 而是靠「後端根本不會下發 61~69 這些權限代號給
 * 非 org=4 的教會」(dao_admin_user.xml 的三支權限查詢帶了 organization 條件)。
 * 所以其他教會登入時, 這個陣列雖然存在, 但 VerticalMenuItem 會把每一項都濾掉,
 * 而切換器根本不會顯示入口。
 */
const gloryNavigationItem = [
    //61 控制面板
    {
        id: 61,
        right: false,
        subject: '控制面板',
        route: 'GloryDashboard',
        icon: 'monitor',
        children: [],
        //無法歸類的放首頁
        attached: [],
    },
    //62 資訊設定
    {
        id: 62,
        right: true,
        subject: '資訊設定',
        route: 'GloryInfo',
        icon: 'setting',
        children: [],
        attached: [],
    },
    //63 團隊管理
    {
        id: 63,
        right: true,
        subject: '團隊管理',
        route: 'GloryTeam',
        icon: 'profile-2user',
        children: [],
        attached: [],
    },
    //64 精選分類
    {
        id: 64,
        right: true,
        subject: '精選分類',
        route: 'GloryFeatured',
        icon: 'category',
        children: [],
        attached: [],
    },
    //65 精選訊息
    {
        id: 65,
        right: true,
        subject: '精選訊息',
        route: 'GloryMessage',
        icon: 'format-square',
        children: [],
        attached: [],
    },
    //66 敬拜管理
    {
        id: 66,
        right: true,
        subject: '敬拜管理',
        route: 'GloryWorship',
        icon: 'audio',
        children: [],
        attached: [],
    },
    //67 活動回顧
    {
        id: 67,
        right: true,
        subject: '活動回顧',
        route: 'GloryEvent',
        icon: 'calendar',
        children: [],
        attached: [],
    },
    //68 關鍵字管理
    {
        id: 68,
        right: true,
        subject: '關鍵字管理',
        route: 'GloryKeyword',
        icon: 'tag',
        children: [],
        attached: [],
    },
    //69 接受耶穌登錄
    {
        id: 69,
        right: true,
        subject: '接受耶穌登錄',
        route: 'GloryAccept',
        icon: 'heart',
        children: [],
        attached: [],
    }
]

export const useNavigationItem = () => {

    const route = useRoute();

    //透過指定的序列排序 (複製後排序, 不變動模組層級常數)
    const adminSortList = [1, 17, 3, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12, 20, 13, 18, 19, 14, 15, 16, 22]
    const adminItems = [...adminNavigationItem].sort((a, b) => {
        return adminSortList.indexOf(a.id) - adminSortList.indexOf(b.id)
    });

    //透過指定的序列排序 (複製後排序, 不變動模組層級常數)
    const shepherdSortList = [51, 52, 53, 54, 55]
    const shepherdItems = [...shepherdNavigationItem].sort((a, b) => {
        return shepherdSortList.indexOf(a.id) - shepherdSortList.indexOf(b.id)
    });

    //透過指定的序列排序 (複製後排序, 不變動模組層級常數)
    const glorySortList = [61, 62, 63, 64, 65, 66, 67, 68, 69]
    const gloryItems = [...gloryNavigationItem].sort((a, b) => {
        return glorySortList.indexOf(a.id) - glorySortList.indexOf(b.id)
    });

    //系統 -> 選單。第三個 portal 進來時把原本的三元運算子改成查表
    const portalItems = {
        [EnumPortalName.ADMIN]: adminItems,
        [EnumPortalName.SHEPHERD]: shepherdItems,
        [EnumPortalName.GLORY]: gloryItems,
    }

    /**
     * 返回指定系統的權限表
     */
    const getMenuItem = computed(() =>{
        return portalItems[route.meta.portal] ?? adminItems
    })

    return {
        getMenuItem
    }

}
